import {
	useWhatsAppAiAgentConfigApprove,
	useWhatsAppAiAgentConfigDisable,
	useWhatsAppAiAgentConfigShow,
	useWhatsAppAiAgentConfigStore,
	type WhatsAppAiAgentConfigData,
	whatsAppAiAgentConfigShowQueryKey,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'

/**
 * A 404 from `show` means the campaign has no config yet, so enabling drafts one first.
 * A 403 means the organisation has not consented to AI using personal data.
 */
export function useWhatsAppAiAgentToggle(campaignId: string) {
	const queryClient = useQueryClient()
	const { data: serverData, isLoading, error } = useWhatsAppAiAgentConfigShow(campaignId, { retry: false })

	const notFound = error?.response?.status === 404
	const consentRequired = error?.response?.status === 403
	const isLoadError = error != null && !notFound && !consentRequired

	const storeMutation = useWhatsAppAiAgentConfigStore()
	const approveMutation = useWhatsAppAiAgentConfigApprove()
	const disableMutation = useWhatsAppAiAgentConfigDisable()

	function applyServer(data: WhatsAppAiAgentConfigData): void {
		queryClient.setQueryData(whatsAppAiAgentConfigShowQueryKey(campaignId), data)
	}

	function approve(): void {
		approveMutation.mutate({ campaignId }, { onSuccess: applyServer })
	}

	function handleEnable(): void {
		if (serverData != null) {
			approve()
			return
		}
		storeMutation.mutate({ campaignId, data: {} }, {
			onSuccess: (data) => {
				applyServer(data)
				approve()
			},
		})
	}

	function handleDisable(): void {
		disableMutation.mutate({ campaignId }, { onSuccess: applyServer })
	}

	return {
		isLoading,
		isLoadError,
		consentRequired,
		enabled: serverData?.enabled ?? false,
		isEnabling: storeMutation.isPending || approveMutation.isPending,
		isDisabling: disableMutation.isPending,
		hasError: storeMutation.isError || approveMutation.isError || disableMutation.isError,
		handleEnable,
		handleDisable,
	}
}
